import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { styled } from '@mui/material/styles'
import { Typography } from '@mui/material'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import { amchartsSetAriaLabel } from '../../utils/amcharts'
import { AMCHART_COLORS, NO_DATA_MESSAGE } from '../../utils/constants'

am5.addLicense(process.env.GATSBY_AMCHARTSV5_LICENSE || '')

const FigureChart = styled('figure')(({ theme }) => ({
  padding: 0,
  marginTop: theme.spacing(4),
  [theme.breakpoints.down('xs')]: {
    marginTop: theme.spacing(3),
  },
}))

const ChartContent = styled('div')(({ theme }) => ({
  display: 'block',
  minHeight: '24rem',
  fontSize: '1rem',
  [theme.breakpoints.down('xs')]: {
    minHeight: '30rem',
  },
}))

export default function HistoricalPremiumDiscountChart({ data }) {
  useEffect(() => {
    if (!data || data.length === 0) {
      return
    }
    const containerId = 'historical-premium-chart'
    const root = am5.Root.new(containerId)
    root.numberFormatter.set('numberFormat', '#.00')

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        pinchZoomX: false,
        paddingLeft: 0,
        layout: root.verticalLayout,
        ariaLabel: 'Historical Premium Chart',
      })
    )

    chart.get('colors').set(
      'colors',
      AMCHART_COLORS.map((color) => am5.color(color))
    )

    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval: { timeUnit: 'day', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, { minorGridEnabled: true }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    )

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, { pan: 'zoom' }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    )

    yAxis.children.unshift(
      am5.Label.new(root, {
        y: am5.percent(50),
        centerY: am5.percent(50),
        text: 'NAV',
        textAlign: 'center',
        rotation: -90,
      })
    )

    chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'none',
        xAxis,
      })
    )

    const createLineSeries = (name, valueYField) => {
      return chart.series.push(
        am5xy.LineSeries.new(root, {
          name,
          xAxis,
          yAxis,
          valueYField,
          valueXField: 'date',
          tooltip: am5.Tooltip.new(root, {
            labelText: '${valueY}',
          }),
          legendRangeLabelText: '{name}',
          legendLabelText: `{name}: $${valueYField}`,
        })
      )
    }

    const navSeries = createLineSeries('NAV', 'nav')
    const marketPriceSeries = createLineSeries('Market Price', 'marketPrice')

    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
      })
    )
    legend.data.setAll(chart.series.values)

    const scrollbarX = chart.set(
      'scrollbarX',
      am5xy.XYChartScrollbar.new(root, {
        orientation: 'horizontal',
        height: 60,
      })
    )
    chart.bottomAxesContainer.children.push(scrollbarX)

    scrollbarX.chart.get('colors').set(
      'colors',
      AMCHART_COLORS.map((color) => am5.color(color))
    )

    const sbNavxAxis = scrollbarX.chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval: { timeUnit: 'day', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, { minorGridEnabled: true }),
      })
    )

    const sbNavyAxis = scrollbarX.chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    )

    const sbNavSeries = scrollbarX.chart.series.push(
      am5xy.LineSeries.new(root, {
        xAxis: sbNavxAxis,
        yAxis: sbNavyAxis,
        valueYField: 'nav',
        valueXField: 'date',
      })
    )

    const sbMarketPricexAxis = scrollbarX.chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.2,
        baseInterval: { timeUnit: 'day', count: 1 },
        renderer: am5xy.AxisRendererX.new(root, { minorGridEnabled: true }),
      })
    )

    const sbMarketPriceyAxis = scrollbarX.chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    )

    const sbMarketPriceSeries = scrollbarX.chart.series.push(
      am5xy.LineSeries.new(root, {
        xAxis: sbMarketPricexAxis,
        yAxis: sbMarketPriceyAxis,
        valueYField: 'marketPrice',
        valueXField: 'date',
      })
    )

    const chartData = data.map((item) => ({
      ...item,
      date: new Date(item.calendarDate).getTime(),
    }))
    navSeries.data.setAll(chartData)
    marketPriceSeries.data.setAll(chartData)
    sbNavSeries.data.setAll(chartData)
    sbMarketPriceSeries.data.setAll(chartData)

    amchartsSetAriaLabel(containerId, 'Historical Premium Chart')

    return () => {
      root.dispose()
    }
  }, [data])

  if (!data || data.length === 0) {
    return <Typography>{NO_DATA_MESSAGE}</Typography>
  }

  return (
    <FigureChart aria-label="Historical Premium and Discount Chart">
      <ChartContent
        id="historical-premium-chart"
        role="img"
        aria-label="Historical Premium Chart"
      />
    </FigureChart>
  )
}

HistoricalPremiumDiscountChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      calendarDate: PropTypes.string.isRequired,
      nav: PropTypes.number.isRequired,
      marketPrice: PropTypes.number.isRequired,
      calendar: PropTypes.shape({
        yearNumber: PropTypes.number.isRequired,
        quarterNumber: PropTypes.number,
      }).isRequired,
    })
  ).isRequired,
}
